import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../../api";
import { LogoMark } from "../../components/Logo";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [otp, setOtp] = useState(null); // { token, email }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", form);
      if (data.requires_2fa) {
        setOtp({ token: data.otp_token, email: form.email.toLowerCase() });
        setBusy(false);
      } else {
        navigate("/admin");
      }
    } catch (err) {
      setError(formatApiError(err, "Could not sign in."));
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/verify-2fa", { otp_token: otp.token, code: code.trim() });
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err, "Could not verify that code."));
      setBusy(false);
    }
  };

  const field =
    "w-full rounded-2xl border border-sage/20 bg-white/80 px-5 py-3.5 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-sage";

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md rounded-[1.8rem] border border-sage/12 bg-white/80 p-10 shadow-[0_20px_50px_-20px_rgba(59,87,75,0.2)] backdrop-blur">
        <div className="flex flex-col items-center text-center">
          <LogoMark size={44} />
          <h1 className="mt-4 font-serif text-3xl text-ink">Owner Studio</h1>
          <p className="mt-2 text-sm text-ink/50">
            {otp ? "Enter the 6-digit code we emailed you" : "Sign in to manage Bloom Paper Studio"}
          </p>
        </div>

        {!otp ? (
          <form onSubmit={submitPassword} className="mt-8 space-y-4">
            <input
              data-testid="admin-email-input"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Email"
              className={field}
            />
            <input
              data-testid="admin-password-input"
              type="password"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Password"
              className={field}
            />
            {error && (
              <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="admin-login-error">{error}</p>
            )}
            <button
              data-testid="admin-login-submit"
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-sage py-4 text-sm font-medium text-cream transition-all duration-300 hover:bg-sage-deep disabled:opacity-60"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />}
              Sign in
            </button>
          </form>
        ) : (
          <form onSubmit={submitCode} className="mt-8 space-y-4">
            <input
              data-testid="twofa-code-input"
              required
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="••••••"
              autoFocus
              className={`${field} text-center font-mono text-2xl tracking-[0.5em]`}
            />
            <p className="text-center text-xs text-ink/45">
              Sent to {otp.email} · expires in 10 minutes
            </p>
            {error && (
              <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="twofa-error">{error}</p>
            )}
            <button
              data-testid="twofa-verify-button"
              disabled={busy || code.length !== 6}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-sage py-4 text-sm font-medium text-cream transition-all duration-300 hover:bg-sage-deep disabled:opacity-60"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={15} />}
              Verify &amp; sign in
            </button>
            <button
              type="button"
              onClick={() => { setOtp(null); setCode(""); setError(""); }}
              className="w-full text-center text-xs text-ink/45 underline underline-offset-2 hover:text-sage"
            >
              Back to password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
